import { Injectable, EventEmitter } from '@angular/core';
import { ActivityActorEvent } from '../model/activityActor-event';
import { Actor } from '../model/actor';
import { ActivityActor } from '../model/activityActor';
import { Location } from '../model/location';
import { SignalRService } from './signalr.service';
import { Activity } from '../model/activity';
import { HttpHelper } from './http.service';
import { SettingsService } from './settings.service';
import { AppConfig } from '../model/config';
import { LocationService } from './location.service';
import { CustomError } from '../model/Errors/customErrors';
import { StateMachineEventEnum } from '../model/stateMachineEvent';
import { BusEnum } from '../common/enums';


@Injectable()
export class ActivityActorService {

  private readonly windowStart: string = this.settings.windowStart.toISOString();
  private readonly windowEnd: string = this.settings.windowEnd.toISOString();
  private readonly uri: string = this.configuration.backendUrl;

  private readonly actors: Actor[] = []

  public readonly activityActors: ActivityActorEvent[] = [];
  public updateActivity$: EventEmitter<ActivityActorEvent>;
  public deleteActivity$: EventEmitter<ActivityActorEvent>;
  public activityActorLoaded$: EventEmitter<boolean>;
  public bfsTransferCreated$: EventEmitter<boolean>;

  constructor(
    private readonly signalRService: SignalRService,
    private readonly http: HttpHelper,
    private readonly settings: SettingsService,
    private readonly configuration: AppConfig,
    private readonly locationService: LocationService) {

    this.updateActivity$ = new EventEmitter<ActivityActorEvent>();
    this.deleteActivity$ = new EventEmitter<ActivityActorEvent>();
    this.activityActorLoaded$ = new EventEmitter<boolean>();
    this.bfsTransferCreated$ = new EventEmitter<boolean>();

    this.signalRService.updateActivity$.subscribe((update: ActivityActor) => {
      this.onCreateActivity(update);
    });

    this.signalRService.deleteActivity$.subscribe((deleted: ActivityActor) => {
      this.onDeleteActivity(deleted);
    });

    this.signalRService.updateActor$.subscribe((actor: Actor) => { this.onUpdateActor(actor); });
    this.signalRService.deleteActor$.subscribe((actor: Actor) => { this.onDeleteActor(actor); });


  }

  public async initService() {
    await this.loadActors(this.uri, this.windowStart, this.windowEnd)
      .then(() => {
        this.loadActivities(this.uri, this.windowStart, this.windowEnd);
      });
  }

  public async assignPlanned(): Promise<any> {

    let body = {};

    return this.http.post(this.configuration.backendUrl + "/api/activity/assignall", body,
      (result) => { },
      (exception) => { }).toPromise<any>();
  }

  public async updateActivity(activity: Activity): Promise<any> {

    return this.http.post(this.configuration.backendUrl + "/api/activity/update", activity,
      (result) => { },
      (exception) => { }).toPromise<any>();
  }

  public async copyActivity(actor: Actor, sourceActivityId: string, targetAvailabilityId: string): Promise<any> {

    let body = {
      "SourceActorId": actor.id,
      "SourceActivityId": sourceActivityId,
      "TargetAvailabilityId": targetAvailabilityId
    };

    return this.http.post(this.configuration.backendUrl + "/api/activity/copyactivity", body,
      (result) => { },
      (exception) => { }).toPromise<any>();

  }

  public async reAssignActivity(currentActivity: Activity, newActivity: Activity): Promise<any> {
    let body = {
      "start": newActivity.start,
      "activityId": currentActivity.id,
      "availabilityId": newActivity.availabilityID,
      "userMappingId": newActivity.userMappingID
    };
    return this.http.post(this.configuration.backendUrl + "/api/state/reassign", body,
      (result) => { },
      (exception) => { }).toPromise<any>();
  }

  public async updateActor(actor: Actor): Promise<any> {

    return this.http.post(this.configuration.backendUrl + "/api/actor/update", actor,
      (result) => { },
      (exception) => { }).toPromise<any>();
  }

  public async updateState(event: string, actor: Actor, activity: Activity, newActivity: Activity = null): Promise<any> {

    if (event.toLowerCase() === "create") {
      let body = {
        "activity": activity,
        "actor": actor,
      };

      return this.http.post(this.configuration.backendUrl + "/api/activity", body,
        (result) => { },
        (exception) => { }).toPromise<any>();

    }
    else if (event.toLowerCase() === "reassign") {
      throw new Error('Should use reAssignActivity instead!');
    }
    else {
      let body = {
        "state": event,
        "activityId": activity.id,
        "userMappingId": activity.userMappingID,
        "availabilityId": activity.availabilityID
      };

      return this.http.post(this.configuration.backendUrl + "/api/state", body,
        (result) => { },
        (exception) => { }).toPromise<any>();
    }
  }

  public async getAllowedEvents(activity: Activity): Promise<any> {

    let url: string = this.configuration.backendUrl + "/api/state/AllowedEvents/" + activity.id;

    return await this.http.get(url, () => { },
      () => { }).toPromise<any>();
  }

  public async confirmNoShow(activityId: string): Promise<any> {
    return this.http.post(this.configuration.backendUrl + "/api/activity/confirmNoShow?activityId=" + activityId, {},
      () => { },
      () => { }).toPromise<any>();
  }

  private async loadActors(uri: string, windowStart: string, windowEnd: string) {
    
    console.log("Load actors");
    await this.http.get(uri + "/api/actor/allinwindow?windowStart=" + windowStart + "&windowEnd=" + windowEnd,
      (data: Actor[]) => {
        this.actors.splice(0, this.actors.length);

        data.forEach((actor: Actor) => {
          this.actors.push(new Actor(actor));
        });
      }, (error) => {
        if (error.status === 401) {
          throw new CustomError(
            {
              name: 'Unauthorized',
              message: 'Unauthorized from server',
              cause: error
            });
        }
    }).toPromise<any>();;
  }

  private async loadActivities(uri: string, windowStart: string, windowEnd: string) {
    console.log("Load activities");
    // activities
    await this.http.get(uri + "/api/activity/allinwindow?windowStart=" + windowStart + "&windowEnd=" + windowEnd, (data: Activity[]) => {
      this.activityActors.splice(0, this.activityActors.length);
      data.forEach((activity: ActivityActor) => {
        let act = new ActivityActor(activity);
        this.activityActors.push(new ActivityActorEvent(act.id, act.state, act.start, act.end, null, act));
      });
      this.activityActorLoaded$.emit(true);

    }, (error) => {
      if (error.status === 401) {
        throw new CustomError(
          {
            name: 'Unauthorized',
            message: 'Unauthorized from server',
            cause: error
          });
      }
    }).toPromise<any>();
  }

  private onCreateActivity(activityActor: ActivityActor) {
    // Grab the start/end locations if the activity is the currently active one or the final one
    if (activityActor.status === 'ACTIVE' || activityActor.state === "Final") {
      let locs = this.locationService.Locations;
      if (locs) {
        activityActor.startLocation = locs.find((l: Location) => l.id == activityActor.startLocationID);
        activityActor.endLocation = locs.find((l: Location) => l.id == activityActor.endLocationID);
      }
    }

    this.notifyOnBfsCreatedTransfer(activityActor);

    //get actor and set it on activity, do only for active?

    activityActor.actor = this.actors.find((a: Actor) => a.id == activityActor.actorID);

    if (activityActor.actor === undefined) {
      console.log("Cannot find actor with id: " + activityActor.actorID);
      return;
    }

    let event = new ActivityActorEvent(activityActor.id, activityActor.state, activityActor.start, activityActor.end, null, activityActor);
    this.handleActivityActorEventCreateAndUpdate(event);
  }

  private notifyOnBfsCreatedTransfer(activityActor: ActivityActor): void {

    if (activityActor?.actor?.type == null) return;

    if (activityActor.actor.type == BusEnum.Transfer &&
      activityActor.state == StateMachineEventEnum.Create &&
      activityActor.modifiedBy === "BFS") {
      console.log("BFS transfer created");
        this.bfsTransferCreated$.emit(true);
    }
  }

  private onDeleteActivity(activity: ActivityActor) {
    console.log("deleteActivity", activity);
    let index = this.activityActors.findIndex((act) => act.id === activity.id);

    if (index === -1) return;

    this.onDeleteActivityEvent(this.activityActors[index]);


  }

  private handleActivityActorEventCreateAndUpdate(activityActorEvent: ActivityActorEvent) {
    if (!this.activityActors) return;

    let index = this.activityActors.findIndex(x => x.activityActor.id === activityActorEvent.id);
    if (index >= 0) {
      // handle update
      this.activityActors.splice(index, 1, activityActorEvent);

    } else {
      // handle add
      this.activityActors.push(activityActorEvent);
    }
    // Raise event here!
    this.updateActivity$.emit(activityActorEvent);
  }

  private onDeleteActivityEvent(ActivityActorEvent: ActivityActorEvent) {
    if (!this.activityActors) return;

    let index = this.activityActors.findIndex(x => x.activityActor.id === ActivityActorEvent.id);
    if (index >= 0) {
      this.activityActors.splice(index, 1);
    }

    this.deleteActivity$.emit(ActivityActorEvent);
  }

  private onUpdateActor(actor: Actor) {

    let index = this.actors.findIndex((act: Actor) => act.id === actor.id);

    if (index === -1) {
      this.actors.push(actor);
    };

    index = this.actors.findIndex((act: Actor) => act.id === actor.id);

    this.actors.splice(index, 1, actor);

    //if actor is updated, all its activities must be updated
    let activities = this.activityActors;
    let actorActivities = activities.filter(a => a.activityActor.actorID == actor.id);
    actorActivities.forEach((activity) => {
      activity.activityActor.actor = actor;
      let actIndex = activities.findIndex((act: ActivityActorEvent) => act.activityActor.id === activity.activityActor.id);
      this.activityActors[actIndex] = activity;

      this.handleActivityActorEventCreateAndUpdate(activity);

    })
  }

  private onDeleteActor(actor: Actor) {
    const index = this.actors.findIndex((act) => act.id === actor.id);

    if (index === -1) return;

    this.actors.splice(index, 1);

    //if actor is deleted, all its activities must be deleted
    let activities = this.activityActors;
    let actorActivities = activities.filter(a => a.activityActor.actorID == actor.id);
    actorActivities.forEach((activity) => {
      let index = activities.findIndex(a => a.activityActor.id == activity.activityActor.id);
      this.onDeleteActivityEvent(this.activityActors[index]);
    })
  }

}
